/* eslint-disable import/prefer-default-export */
import { defineStore } from 'pinia';
import { computed, reactive, ref } from 'vue';
import { useMutation, useQuery } from '@pinia/colada';
import ConflictCheckRecordService from '../services/ConflictCheckRecordService';
import { CONFLICT_LOG_PAGE_SIZE } from '../constants/conflictRecordConstants';
import { withoutGatedRows } from '../utils/conflictPermissionHelpers';
import { useConflictCheckStore } from './useConflictCheckStore';
import type {
  ConflictCheckNotesRefusal,
  ConflictCheckRecordDetail,
  ConflictCheckRecordFilter,
  ConflictCheckRecordSummary,
  ConflictResultRow,
} from '../models/conflictTypes';

interface HttpError {
  response?: { status?: number; data?: unknown };
}

const statusOf = (e: unknown): number | undefined =>
  (e as HttpError)?.response?.status;

/**
 * Saved conflict checks: the firm-wide log and the one record being viewed.
 *
 * Both reads are `staleTime: 0` + `.refetch(true)`. The log changes whenever anyone at
 * the firm saves, and a record's `notesEditable` depends on the clock, so neither is
 * safe to serve from cache. Filter and page are in the log's key so a background
 * refetch cannot write one page into another's entry.
 */
export const useConflictCheckRecordStore = defineStore(
  'conflictCheckRecord',
  () => {
    const filter = reactive<ConflictCheckRecordFilter>({
      from: null,
      to: null,
      page: 1,
      pageSize: CONFLICT_LOG_PAGE_SIZE,
    });
    const items = ref<ConflictCheckRecordSummary[]>([]);
    const total = ref(0);
    const logLoading = ref(false);
    const logError = ref<unknown>(null);
    const logLoaded = ref(false);

    const record = ref<ConflictCheckRecordDetail | null>(null);
    const recordLoading = ref(false);
    const recordError = ref<unknown>(null);
    const recordId = ref<string | null>(null);
    /** Set by a save, which hands over the record it created; consumed on first load. */
    let handedOverId: string | null = null;

    const savingNotes = ref(false);
    /** The server's refusal, kept so the page can say why the notes did not change. */
    const notesRefusal = ref<ConflictCheckNotesRefusal | null>(null);

    const recordNotFound = computed(() => statusOf(recordError.value) === 404);
    const isFiltered = computed(() => Boolean(filter.from || filter.to));

    /** The snapshot's rows under the same permission gate as a live check. */
    const permitted = (rows: ConflictResultRow[]): ConflictResultRow[] =>
      withoutGatedRows(rows, useConflictCheckStore().canSeeBillingInstructions);

    const fullMatches = computed(() =>
      permitted(record.value?.result.fullMatches ?? []),
    );
    const partialMatches = computed(() =>
      permitted(record.value?.result.partialMatches ?? []),
    );
    const allRows = computed(() => [
      ...fullMatches.value,
      ...partialMatches.value,
    ]);
    const searchableParties = computed(
      () =>
        record.value?.result.parties.filter((party) => party.searchable) ?? [],
    );

    const logQuery = useQuery({
      key: () => [
        'conflict-check-log',
        filter.from ?? '',
        filter.to ?? '',
        String(filter.page),
        String(filter.pageSize),
      ],
      query: () => ConflictCheckRecordService.list({ ...filter }),
      enabled: false,
      staleTime: 0,
    });

    let logToken = 0;

    async function loadLog(): Promise<void> {
      const token = ++logToken;
      logLoading.value = true;
      logError.value = null;
      try {
        const state = await logQuery.refetch(true);
        if (token !== logToken) return;
        items.value = state.data?.items ?? [];
        total.value = state.data?.total ?? 0;
        logLoaded.value = true;
      } catch (e) {
        if (token !== logToken) return;
        // An error must never render as the empty log — they mean different things.
        items.value = [];
        total.value = 0;
        logLoaded.value = false;
        logError.value = e;
        throw e;
      } finally {
        if (token === logToken) logLoading.value = false;
      }
    }

    function setDateRange(from: string | null, to: string | null): void {
      filter.from = from;
      filter.to = to;
      filter.page = 1;
    }

    function setPage(page: number): void {
      filter.page = page;
    }

    function setPageSize(pageSize: number): void {
      filter.pageSize = pageSize;
      filter.page = 1;
    }

    const recordQuery = useQuery({
      key: () => ['conflict-check-record', recordId.value ?? ''],
      query: () =>
        recordId.value
          ? ConflictCheckRecordService.get(recordId.value)
          : Promise.resolve(null),
      enabled: false,
      staleTime: 0,
    });

    let recordToken = 0;

    async function loadRecord(id: string): Promise<void> {
      const token = ++recordToken;
      const handedOver = handedOverId === id && record.value?.id === id;
      handedOverId = null;
      if (handedOver) return;
      recordId.value = id;
      record.value = null;
      recordLoading.value = true;
      recordError.value = null;
      notesRefusal.value = null;
      try {
        const state = await recordQuery.refetch(true);
        if (token !== recordToken) return;
        record.value = state.data ?? null;
        if (!state.data)
          throw new Error('The saved check could not be loaded.');
      } catch (e) {
        if (token !== recordToken) return;
        recordError.value = e;
        throw e;
      } finally {
        if (token === recordToken) recordLoading.value = false;
      }
    }

    function setRecord(detail: ConflictCheckRecordDetail): void {
      recordToken += 1;
      handedOverId = detail.id;
      recordId.value = detail.id;
      record.value = detail;
      recordError.value = null;
      recordLoading.value = false;
      notesRefusal.value = null;
      // The log is now missing this check; the next visit has to fetch it again.
      logLoaded.value = false;
    }

    const notesMutation = useMutation({
      mutation: ({ id, notes }: { id: string; notes: string }) =>
        ConflictCheckRecordService.updateNotes(id, notes),
    });

    /**
     * On a 403 the server's word is final: the notes stay as stored and the record
     * stops offering an edit, whatever the page thought a moment ago (ERR-011/012).
     */
    let notesToken = 0;

    async function saveNotes(notes: string): Promise<void> {
      const current = record.value;
      if (!current) return;
      const token = ++notesToken;
      savingNotes.value = true;
      notesRefusal.value = null;
      try {
        const updated = await notesMutation.mutateAsync({
          id: current.id,
          notes,
        });
        if (record.value?.id !== current.id) return;
        record.value = updated;
        logLoaded.value = false;
      } catch (e) {
        if (statusOf(e) === 403 && record.value?.id === current.id) {
          notesRefusal.value =
            ((e as HttpError).response?.data as ConflictCheckNotesRefusal) ??
            null;
          record.value = { ...current, notesEditable: false };
        }
        throw e;
      } finally {
        if (token === notesToken) savingNotes.value = false;
      }
    }

    function clearRecord(): void {
      recordToken += 1;
      recordId.value = null;
      record.value = null;
      recordError.value = null;
      recordLoading.value = false;
      notesRefusal.value = null;
    }

    function reset(): void {
      clearRecord();
      logToken += 1;
      items.value = [];
      total.value = 0;
      logError.value = null;
      logLoading.value = false;
      logLoaded.value = false;
      setDateRange(null, null);
    }

    return {
      filter,
      items,
      total,
      logLoading,
      logError,
      logLoaded,
      isFiltered,
      record,
      recordLoading,
      recordError,
      recordNotFound,
      fullMatches,
      partialMatches,
      allRows,
      searchableParties,
      savingNotes,
      notesRefusal,
      loadLog,
      setDateRange,
      setPage,
      setPageSize,
      loadRecord,
      setRecord,
      saveNotes,
      clearRecord,
      reset,
    };
  },
);
